import { SignInButton, SignedIn, SignedOut, UserButton, useAuth } from "@clerk/clerk-react";
import { WS_EVENT } from "@the-drop/types";
import { userHasPlatformAdminRole } from "@the-drop/types";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DispensaryTopBar } from "../components/DispensaryTopBar";
import { DispensarySidebar } from "../layouts/Sidebar";
import { resolveClerkPublishableKey } from "../clerk-env";
import {
  clearAdminDispensaryScope,
  readAdminDispensaryScope,
  writeAdminDispensaryScope,
} from "../lib/admin-dispensary-scope";
import {
  fetchAdminDispensariesList,
  fetchMeDispensaryMemberships,
  type DispensaryPublicSummary,
  type MeDispensaryMembership,
  type MeDispensaryMembershipsResponse,
} from "../lib/dispensary-account-api";

/** Sentinel scope value meaning "All locations" (aggregate view), persisted like a dispensary id. */
const ALL_LOCATIONS_SCOPE = "__all__";
import { isDispensaryOpsUser, resolveDispensaryIdForWorkspaceUser } from "../lib/dispensary-access";
import { fetchMe, type MeUser } from "../lib/fetch-me";
import { playOrderReceivedChime } from "../lib/play-order-received-sound";
import { useDispensarySocket } from "../lib/use-dispensary-socket";
import { DispensaryWorkspaceProvider } from "./dispensary-workspace-context";
import { DispensarySocketProvider } from "./dispensary-socket-context";
import { WorkspaceMeProvider } from "./workspace-me-context";

function workspacePageTitle(pathname: string): string {
  if (pathname.startsWith("/workspace/dashboard")) return "Dashboard";
  if (pathname.startsWith("/workspace/orders")) return "Orders";
  if (pathname.startsWith("/workspace/inventory")) return "Inventory";
  if (pathname.startsWith("/workspace/runs")) return "Runs";
  if (pathname.startsWith("/workspace/invoices")) return "Invoices";
  if (pathname.startsWith("/workspace/settlements")) return "Invoices";
  if (pathname.startsWith("/workspace/onboarding")) return "Onboarding";
  if (pathname.startsWith("/workspace/account")) return "Account";
  if (pathname.startsWith("/workspace/admin/create-dispensary")) return "Create dispensary";
  return "Dispensary";
}

export function WorkspaceLayout() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="min-h-screen bg-bg px-4 py-12">
        <div className="card-surface mx-auto max-w-lg p-6 text-sm text-text">
          Set <span className="font-mono text-dark">VITE_CLERK_PUBLISHABLE_KEY</span> in{" "}
          <span className="font-mono text-dark">apps/dispensary-web/.env</span> to use the workspace (Clerk hooks require
          a provider).
        </div>
      </div>
    );
  }
  return <WorkspaceLayoutWithClerk />;
}

function WorkspaceLayoutWithClerk() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locationSearch = useRouterState({ select: (s) => s.location.search });
  const navigate = useNavigate();
  const isDashboard = pathname.startsWith("/workspace/dashboard");
  const qFromDashboardUrl = isDashboard ? new URLSearchParams(locationSearch).get("q")?.trim() ?? "" : "";
  const [searchDraft, setSearchDraft] = useState(qFromDashboardUrl);

  useEffect(() => {
    setSearchDraft(qFromDashboardUrl);
  }, [qFromDashboardUrl]);
  const [me, setMe] = useState<MeUser | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [adminScope, setAdminScope] = useState<string | null>(() => readAdminDispensaryScope());
  // Org-scoped (multi-location) staff: their brand + locations, for the switcher
  // and the aggregate "All locations" view. Null for admins / single-location staff.
  const [orgMemberships, setOrgMemberships] = useState<MeDispensaryMembershipsResponse | null>(null);

  const pageTitle = workspacePageTitle(pathname);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setMe(null);
      setMeError(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchMe(getToken);
        if (!cancelled) {
          setMe(r.user);
          setMeError(null);
        }
      } catch {
        if (!cancelled) {
          setMe(null);
          setMeError("Could not load your session.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  // Load the org-scoped user's locations once (the switcher list + the orgId
  // the aggregate view needs). Only for org-scoped staff (no single store).
  useEffect(() => {
    if (!me || userHasPlatformAdminRole(me) || !isDispensaryOpsUser(me) || me.dispensaryId) {
      setOrgMemberships(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchMeDispensaryMemberships(getToken);
        if (!cancelled) setOrgMemberships(r);
      } catch {
        if (!cancelled) setOrgMemberships(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me, getToken]);

  const orgId = orgMemberships?.orgId ?? null;
  const allLocations =
    adminScope === ALL_LOCATIONS_SCOPE &&
    orgId !== null &&
    (orgMemberships?.memberships.length ?? 0) > 1;

  const dispensaryId = useMemo(() => {
    if (!me) return null;
    // "All locations" is an aggregate read — no single active store.
    if (adminScope === ALL_LOCATIONS_SCOPE) return null;
    return resolveDispensaryIdForWorkspaceUser(me, adminScope);
  }, [me, adminScope]);

  const onSaveAdminScope = useCallback((raw: string) => {
    const id = raw.trim();
    if (id.length === 0) {
      clearAdminDispensaryScope();
      setAdminScope(null);
      return;
    }
    writeAdminDispensaryScope(id);
    setAdminScope(id);
  }, []);

  const onDispensaryWsEvent = useCallback((eventName: string) => {
    if (eventName === WS_EVENT.ORDER_RECEIVED) {
      playOrderReceivedChime();
    }
  }, []);

  const { socket } = useDispensarySocket(
    dispensaryId,
    getToken,
    Boolean(isSignedIn && me && isDispensaryOpsUser(me) && dispensaryId),
    onDispensaryWsEvent,
  );

  const opsShell = Boolean(isSignedIn && me && isDispensaryOpsUser(me));
  const isDashboardRoute = pathname.startsWith("/workspace/dashboard");

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-6 py-16">
        <p className="text-sm text-gray">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <p className="hidden max-[959px]:block border-b border-amber-light bg-amber-bg px-4 py-2 text-center text-2xs font-medium text-amber-900">
        Best viewed on a desktop display (1200px or wider recommended).
      </p>

      <SignedOut>
        <>
          <DispensaryTopBar />
          <main className="mx-auto max-w-lg space-y-4 px-6 py-16">
            <p className="text-sm text-gray">Sign in with a dispensary staff or platform admin account.</p>
            <SignInButton mode="modal">
              <button type="button" className="btn-primary">
                Sign in
              </button>
            </SignInButton>
          </main>
        </>
      </SignedOut>

      <SignedIn>
        {!opsShell ? (
          <>
            <DispensaryTopBar />
            <main className="mx-auto max-w-7xl px-6 py-8">
              {!isLoaded || (isSignedIn && me === null && meError === null) ? (
                <p className="text-sm text-gray">Loading profile…</p>
              ) : null}
              {meError ? <p className="text-sm text-red">{meError}</p> : null}
              {me && !isDispensaryOpsUser(me) ? (
                <div className="card-surface p-6 text-sm text-text">
                  <p className="font-medium text-dark">This portal is for dispensary operations staff.</p>
                  <p className="mt-2 text-gray">
                    Your roles:{" "}
                    <span className="font-mono text-text">
                      {me.roles != null && me.roles.length > 0 ? me.roles.join(", ") : me.role}
                    </span>
                    . Use the customer or transporter app instead.
                  </p>
                </div>
              ) : null}
            </main>
          </>
        ) : (
          <WorkspaceMeProvider user={me!}>
            <div className="flex min-h-screen">
              <DispensarySidebar />

              <div className="ml-[220px] flex min-h-screen flex-1 flex-col">
              <header className="fixed left-[220px] right-0 top-0 z-20 flex h-16 min-h-[4rem] items-center gap-2 border-b border-[#EBEBEB] bg-white px-4 sm:px-7">
                <h1 className="shrink-0 text-lg font-bold text-[#111111] sm:text-xl">{pageTitle}</h1>
                <div className="min-w-0 flex-1 px-1 sm:px-2">
                  <label className="relative block">
                    <span className="sr-only">Search dashboard</span>
                    <WorkspaceSearchIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#555555]" />
                    <input
                      type="search"
                      name="q"
                      value={searchDraft}
                      title={isDashboard ? undefined : "Press Enter to search the dashboard"}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchDraft(v);
                        if (!isDashboard) return;
                        void navigate({
                          to: "/workspace/dashboard",
                          search: v.trim() ? { q: v.trim() } : {},
                          replace: true,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const v = e.currentTarget.value.trim();
                        void navigate({
                          to: "/workspace/dashboard",
                          search: v ? { q: v } : {},
                        });
                      }}
                      placeholder="Search orders, transporters, customers…"
                      autoComplete="off"
                      className="h-10 w-full max-w-2xl rounded-full border border-[#EBEBEB] bg-[#F5F5F3] py-2 pl-10 pr-3 text-sm text-[#111111] placeholder:text-[#888888] focus:border-[#111111] focus:outline-none focus:ring-1 focus:ring-[#111111]"
                    />
                  </label>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:gap-4">
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: "h-8 w-8",
                      },
                    }}
                  />
                </div>
              </header>

              <main className={`flex-1 overflow-y-auto pt-16 ${isDashboardRoute ? "bg-[#F5F5F3]" : "bg-bg"}`}>
                <div className={isDashboardRoute ? "w-full" : "mx-auto max-w-7xl p-6"}>
                  {!isLoaded || (isSignedIn && me === null && meError === null) ? (
                    <p className="text-sm text-gray">Loading profile…</p>
                  ) : null}
                  {meError ? <p className="text-sm text-red">{meError}</p> : null}

                  {me && userHasPlatformAdminRole(me) && !pathname.startsWith("/workspace/admin/create-dispensary") ? (
                    <section className="card-surface mb-6 p-6">
                      <h2 className="text-sm font-medium text-dark">Platform admin · dispensary scope</h2>
                      <p className="mt-1 text-xs text-gray">
                        Choose which dispensary to scope for Socket.io{" "}
                        <span className="font-mono text-text">/ws/dispensary/:id</span> and REST. Stored in{" "}
                        <span className="font-mono text-text">sessionStorage</span> for this tab.
                      </p>
                      <AdminScopeForm
                        getToken={getToken}
                        initial={adminScope ?? ""}
                        onSave={onSaveAdminScope}
                      />
                    </section>
                  ) : null}

                  {/* Org-scoped staff (no single dispensaryId): pick which of their
                      brand's locations to operate. Single-location staff skip this. */}
                  {me && !userHasPlatformAdminRole(me) && isDispensaryOpsUser(me) && !me.dispensaryId ? (
                    <section className="card-surface mb-6 p-6">
                      <h2 className="text-sm font-medium text-dark">Location</h2>
                      <p className="mt-1 text-xs text-gray">
                        You manage multiple stores — choose which location to operate. Saved for this tab.
                      </p>
                      <OrgLocationSwitcher
                        list={orgMemberships?.memberships ?? null}
                        initial={adminScope ?? ""}
                        onSave={onSaveAdminScope}
                      />
                    </section>
                  ) : null}

                  {!isDashboardRoute && !dispensaryId && me && userHasPlatformAdminRole(me) ? (
                    <p className="mb-6 text-sm text-amber">Select a dispensary above to enable the socket.</p>
                  ) : null}
                  {!isDashboardRoute && !dispensaryId && me && !userHasPlatformAdminRole(me) && me.dispensaryId ? (
                    <p className="mb-6 text-sm text-amber">
                      Your user has no <span className="font-mono text-text">dispensaryId</span>; ask an admin to link
                      this account to a dispensary.
                    </p>
                  ) : null}

                  <DispensaryWorkspaceProvider value={{ dispensaryId, orgId, allLocations, getToken }}>
                    <DispensarySocketProvider socket={socket}>
                      <Outlet />
                    </DispensarySocketProvider>
                  </DispensaryWorkspaceProvider>
                </div>
              </main>
            </div>
            </div>
          </WorkspaceMeProvider>
        )}
      </SignedIn>
    </div>
  );
}

function WorkspaceSearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function dispensaryOptionLabel(d: DispensaryPublicSummary): string {
  const name = d.dbaName?.trim() || d.legalName;
  const slug = d.publicSlug ? ` · /${d.publicSlug}` : "";
  const inactive = d.isActive ? "" : " (inactive)";
  return `${name}${slug}${inactive}`;
}

function AdminScopeForm({
  getToken,
  initial,
  onSave,
}: {
  getToken: () => Promise<string | null>;
  initial: string;
  onSave: (id: string) => void;
}) {
  const [list, setList] = useState<DispensaryPublicSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchAdminDispensariesList(getToken);
        if (cancelled) return;
        if (rows === null) {
          setList([]);
          setLoadError("Could not load dispensaries (admin list).");
        } else {
          setList([...rows].sort((a, b) => dispensaryOptionLabel(a).localeCompare(dispensaryOptionLabel(b))));
          setLoadError(null);
        }
      } catch {
        if (!cancelled) {
          setList([]);
          setLoadError("Could not load dispensaries.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const unknownScopeInList = useMemo(() => {
    if (!initial.trim()) return false;
    return !(list ?? []).some((d) => d.id === initial.trim());
  }, [initial, list]);

  return (
    <div className="mt-3 max-w-xl">
      <label className="block text-xs text-gray">
        Dispensary
        <select
          className="input-field mt-1"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v);
            onSave(v);
          }}
          disabled={list === null}
        >
          <option value="">— Select a dispensary —</option>
          {unknownScopeInList ? (
            <option value={initial.trim()}>
              {initial.trim().slice(0, 8)}…{initial.trim().slice(-4)} (saved scope, not in list)
            </option>
          ) : null}
          {(list ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {dispensaryOptionLabel(d)}
            </option>
          ))}
        </select>
      </label>
      {list === null ? <p className="mt-1 text-xs text-gray">Loading dispensaries…</p> : null}
      {loadError ? <p className="mt-1 text-xs text-amber">{loadError}</p> : null}
    </div>
  );
}

function membershipOptionLabel(m: MeDispensaryMembership): string {
  return m.locationLabel?.trim() ? `${m.name} · ${m.locationLabel}` : m.name;
}

/**
 * Location picker for org-scoped (multi-location) staff. The membership `list`
 * is loaded once by the layout. When the user has more than one store, an
 * "All locations" option (aggregate view) is offered alongside each store.
 */
function OrgLocationSwitcher({
  list,
  initial,
  onSave,
}: {
  list: MeDispensaryMembership[] | null;
  initial: string;
  onSave: (id: string) => void;
}) {
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const sorted = useMemo(
    () =>
      list === null
        ? null
        : [...list].sort((a, b) => membershipOptionLabel(a).localeCompare(membershipOptionLabel(b))),
    [list],
  );

  if (sorted !== null && sorted.length === 0) {
    return (
      <p className="mt-3 text-sm text-amber">
        Your account isn’t linked to any dispensary; ask an admin to add you to a location.
      </p>
    );
  }

  const showAll = (sorted?.length ?? 0) > 1;

  return (
    <div className="mt-3 max-w-xl">
      <label className="block text-xs text-gray">
        Location
        <select
          className="input-field mt-1"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v);
            onSave(v);
          }}
          disabled={sorted === null}
        >
          <option value="">— Select a location —</option>
          {showAll ? <option value={ALL_LOCATIONS_SCOPE}>All locations</option> : null}
          {(sorted ?? []).map((m) => (
            <option key={m.dispensaryId} value={m.dispensaryId}>
              {membershipOptionLabel(m)}
            </option>
          ))}
        </select>
      </label>
      {sorted === null ? <p className="mt-1 text-xs text-gray">Loading locations…</p> : null}
    </div>
  );
}
